import React from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  X,
  FileText,
  Image as ImageIcon,
  Trash2
} from "lucide-react";

const FilePreviewLightbox = ({
  previewFile,
  onClose,
  onRemoveFile,
  userRole = "designer"
}) => {
  if (!previewFile) return null;

  return (
    <Dialog
      open={Boolean(previewFile)}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 1500 }}
      PaperProps={{
        sx: {
          borderRadius: "14px",
          overflow: "hidden"
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e2e8f0",
          py: 1.5,
          px: 2.5
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              p: 0.75,
              borderRadius: "6px",
              backgroundColor: "#eff6ff",
              color: "#1877f2"
            }}
          >
            {previewFile.url ? (
              <ImageIcon size={18} />
            ) : (
              <FileText size={18} />
            )}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.875rem" }}>
              {previewFile.name}
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.72rem" }}>
              Size: {previewFile.size} • Uploaded: {previewFile.uploadedAt || "Recently"}
            </Typography>
          </Box>
        </Box>

        <IconButton onClick={onClose} size="small" sx={{ color: "#94a3b8" }}>
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, backgroundColor: "#f8fafc", textAlign: "center" }}>
        {(() => {
          const fileName = (previewFile.name || "").toLowerCase();
          const fileType = (previewFile.type || "").toLowerCase();
          const isImage =
            fileType.startsWith("image/") ||
            /\.(jpe?g|png|gif|webp|svg|bmp|jfif)$/i.test(fileName);
          const isPdf =
            fileType.includes("pdf") || fileName.endsWith(".pdf");

          if (previewFile.url && isImage) {
            return (
              <Box
                sx={{
                  maxHeight: "70vh",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#0f172a",
                  borderRadius: "10px",
                  p: 1.5
                }}
              >
                <img
                  src={previewFile.url}
                  alt={previewFile.name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "65vh",
                    objectFit: "contain",
                    borderRadius: 6
                  }}
                />
              </Box>
            );
          }

          if (previewFile.url && isPdf) {
            return (
              <Box
                sx={{
                  width: "100%",
                  height: "70vh",
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#ffffff"
                }}
              >
                <iframe
                  src={previewFile.url}
                  title={previewFile.name}
                  width="100%"
                  height="100%"
                  style={{ border: "none", width: "100%", height: "100%" }}
                />
              </Box>
            );
          }

          const isDocx = /\.(docx?|odt|rtf|pptx?|xlsx?)$/i.test(fileName);
          if (previewFile.url && isDocx) {
            const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(previewFile.url)}&embedded=true`;
            return (
              <Box
                sx={{
                  width: "100%",
                  height: "70vh",
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#ffffff"
                }}
              >
                <iframe
                  src={viewerUrl}
                  title={previewFile.name}
                  width="100%"
                  height="100%"
                  style={{ border: "none", width: "100%", height: "100%" }}
                />
              </Box>
            );
          }

          // Fallback if URL is available for other document types
          if (previewFile.url) {
            return (
              <Box
                sx={{
                  width: "100%",
                  height: "70vh",
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#ffffff"
                }}
              >
                <iframe
                  src={previewFile.url}
                  title={previewFile.name}
                  width="100%"
                  height="100%"
                  style={{ border: "none", width: "100%", height: "100%" }}
                />
              </Box>
            );
          }

          return (
            <Paper
              sx={{
                p: 5,
                borderRadius: "12px",
                border: "1px dashed #cbd5e1",
                backgroundColor: "#ffffff"
              }}
            >
              <FileText
                size={48}
                color="#1877f2"
                style={{ margin: "0 auto 12px" }}
              />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#0f172a" }}
              >
                {previewFile.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#64748b", mt: 0.5, fontSize: "0.8125rem" }}
              >
                Document File ({previewFile.type || "PDF / DOCX"})
              </Typography>
            </Paper>
          );
        })()}
      </DialogContent>

      <DialogActions
        sx={{
          p: 1.5,
          px: 2.5,
          borderTop: "1px solid #e2e8f0",
          justifyContent: "space-between"
        }}
      >
        {userRole !== "admin" && onRemoveFile ? (
          <Button
            color="error"
            size="small"
            startIcon={<Trash2 size={15} />}
            onClick={() => {
              onRemoveFile(previewFile.id);
              onClose();
            }}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}
          >
            Delete File
          </Button>
        ) : (
          <Box />
        )}

        <Button
          variant="contained"
          size="small"
          onClick={onClose}
          sx={{
            textTransform: "none",
            borderRadius: "6px",
            fontWeight: 600,
            fontSize: "0.78rem",
            backgroundColor: "#1877f2"
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilePreviewLightbox;
